import { ChangeDetectionStrategy, Component, forwardRef, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart } from '@angular/router';
import { ProductViewComponentDef } from '@df/catalog-ui/product/view/product-view.component.def';
import { IProductConfigurableSelection, IProductDataV3 } from '@df/catalog/product/product';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { ICmsBlock, NICmsBlock } from '@icms/core/resource/block/icms-block';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { filter } from 'rxjs/internal/operators/filter';
import { ProductDecorator } from '../../../model/product/product.decorator';
import { ProductViewExternalComponent } from './external/product-view-external.component';
import { ProductViewLayoutComponent } from './layout/product-view-layout.component';
import { IProductViewLayoutContent } from './layout/product-view-layout.interface';

@Component({
    selector: 'product-view',
    templateUrl: './product-view.component.html',
    standalone: true,
    imports: [forwardRef(() => ProductViewExternalComponent), forwardRef(() => ProductViewLayoutComponent)],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewComponent extends ProductViewComponentDef<ProductDecorator<IProductDataV3>> implements OnInit, OnDestroy {
    showExternal = false;
    productLayoutModel!: ICmsBlock<NICmsBlock.IData<IProductViewLayoutContent>>;
    override canDetach = false;

    private readonly afterContentInit = inject(AfterContentInitDirectiveService);
    private readonly iCmsBlockResource = inject(ICmsBlockResource);
    private readonly disposableTimeoutService = injectTimeoutService();

    override ngOnInit(): void {
        super.ngOnInit();

        this.showExternal = !!this.model.ppExternalLayoutVariant;

        if (this.showExternal) {
            this.iCmsBlockResource
                .retrieve<IProductViewLayoutContent>(
                    {
                        bareUrl: 'product-page-layout',
                        variant: this.model.ppLayoutVariant
                    },
                    undefined
                )
                .then((model: ICmsBlock<NICmsBlock.IData<IProductViewLayoutContent>>) => {
                    this.productLayoutModel = model;

                    this.detectChanges();
                });

            this.subscriptions.add(
                this.app.meta.routerEvents.pipe(filter(evt => evt instanceof NavigationStart)).subscribe(() => this.closeExternal())
            );
        }

        this.afterContentInit.lock(this);

        this.disposableTimeoutService.setTimeout(() => {
            this.afterContentInit.unlock(this);
        }, 8000);
    }

    override ngOnDestroy(): void {
        this.afterContentInit.unlock(this);

        super.ngOnDestroy();
    }

    closeExternal(): void {
        this.showExternal = false;

        this.detectChanges();
    }

    /**
     * We need to re check component even when no change is applied
     * as some things depend on configurableSelection object that change calls this thing
     */
    override matchExactly(selection: IProductConfigurableSelection): boolean {
        const modelId = this.model?.id;
        const child = super.matchExactly(selection);

        if (this.model?.id !== modelId) {
            this.detectChanges();
        }

        return child;
    }
}
